import React, { useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../../../store";
import { addFuelSystem } from "../../../../store/features/optionalSystemsSlice";
import { FuelSystem } from "../../../../types/optionalSystems";
import { formatCurrency } from "../../../../utils/formatting";
import { selectors } from "../../../../store/selectors";
import TonnageInput from "../../../common/TonnageInput";

interface FuelSystemsProps {
  availableSystems: FuelSystem[];
  shipTonnage: number;
  isStreamlined: boolean;
  techLevel: number;
  tlOverrideActive?: boolean;
}

// Mapping from subtype to display name
const FUEL_SYSTEM_NAMES: Record<string, string> = {
  collapsibleTank: "Collapsible Fuel Tank",
  dropTank: "Drop Tank",
  fuelCargoContainer: "Fuel/Cargo Container",
  fuelProcessor: "Fuel Processor",
  fuelScoop: "Fuel Scoop",
  fuelTankCompartment: "Fuel Tank Compartment",
  metalHydride: "Metal Hydride Storage",
  mountableTank: "Mountable Tank",
  ramscoop: "Ramscoop",
};

const FuelSystems: React.FC<FuelSystemsProps> = ({
  availableSystems,
  shipTonnage,
  isStreamlined,
  techLevel,
  tlOverrideActive = false,
}) => {
  const dispatch = useAppDispatch();
  const systems = useAppSelector((state) => state.optionalSystems.fuel);
  const shipState = useAppSelector((state) => state.shipBuilder);

  const [selectedSystem, setSelectedSystem] = React.useState<
    Partial<FuelSystem>
  >({
    type: "fuel",
    subtype: "collapsibleTank",
    name: FUEL_SYSTEM_NAMES["collapsibleTank"],
    cost: 0,
    tonnage: 0,
    power: 0,
    properties: {},
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showErrors, setShowErrors] = React.useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [compartmentPercent, setCompartmentPercent] = React.useState<number>(1);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [additionalRamscoopTonnage, setAdditionalRamscoopTonnage] =
    React.useState<number>(0);

  // Get canonical fuel tankage from Redux state
  const canonicalFuelTonnage = useAppSelector(
    (state) => state.shipBuilder.fuelTanks || 0,
  );

  // Sum up optional fuel systems (excluding compartments)
  const optionalFuelTonnage = systems
    .filter((s) => s.subtype !== "fuelTankCompartment")
    .reduce((sum, s) => sum + (s.tonnage || 0), 0);

  // Use the greater of canonical or optional, or sum if both are meaningful
  const totalFuelTonnage = Math.max(canonicalFuelTonnage, optionalFuelTonnage);

  // Calculate compartment tonnage based on percentage
  const compartmentTonnage =
    selectedSystem.subtype === "fuelTankCompartment"
      ? Math.round(totalFuelTonnage * (compartmentPercent / 100) * 100) / 100
      : selectedSystem.tonnage || 0;

  // Ramscoop calculations using selector
  const ramscoopStats = useAppSelector((state) => {
    const selector = selectors.derived.createRamscoopStatsSelector(
      shipTonnage,
      additionalRamscoopTonnage,
    );
    return selector(state);
  });
  const totalRamscoopTonnage = ramscoopStats.tonnage;
  const ramscoopCost = ramscoopStats.cost;
  const ramscoopCollection = ramscoopStats.collectionRate;

  // Top-level state for per-card input and error
  const [cardInputs, setCardInputs] = React.useState<{
    [subtype: string]: any;
  }>({});
  const [cardErrors, setCardErrors] = React.useState<{
    [subtype: string]: string | null;
  }>({});

  // Precompute all selector results for each system subtype
  const collapsibleTankStats = useAppSelector((state) =>
    selectors.derived.selectCollapsibleTankStats(
      state,
      cardInputs.collapsibleTank || 1,
    ),
  );
  const fuelProcessorStats = useAppSelector((state) =>
    selectors.derived.selectFuelProcessorStats(
      state,
      cardInputs.fuelProcessor || 1,
    ),
  );
  const fuelTankCompartmentStats = useAppSelector((state) =>
    selectors.derived.selectFuelTankCompartmentStats(
      state,
      cardInputs.fuelTankCompartment || 1,
    ),
  );
  const mountableTankStats = useAppSelector((state) =>
    selectors.derived.selectMountableTankStats(
      state,
      cardInputs.mountableTank || 1,
    ),
  );
  const dropTankStats = useAppSelector((state) =>
    selectors.derived.selectDropTankStats(state, cardInputs.dropTank || 1),
  );
  const fuelCargoContainerStats = useAppSelector((state) => {
    const allocation = cardInputs[`fuelCargoContainer_allocation`] || "fuel";
    const selector = selectors.derived.createFuelCargoContainerStatsSelector(
      cardInputs.fuelCargoContainer || 1,
      allocation,
    );
    return selector(state);
  });

  // Add this useEffect after all Ramscoop calculations
  useEffect(() => {
    if (selectedSystem.subtype === "ramscoop") {
      setSelectedSystem((prev) => ({
        ...prev,
        tonnage: totalRamscoopTonnage,
        cost: ramscoopCost,
        properties: {
          ...prev.properties,
          collectionRate: ramscoopCollection,
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedSystem.subtype,
    totalRamscoopTonnage,
    ramscoopCost,
    ramscoopCollection,
  ]);

  // Validation logic
  const validationErrors: string[] = [];
  if (!selectedSystem.subtype) {
    validationErrors.push("Please select a fuel system subtype.");
  }
  if (selectedSystem.subtype === "fuelTankCompartment") {
    if (compartmentPercent < 1 || compartmentPercent > 99) {
      validationErrors.push("Compartment percentage must be between 1 and 99.");
    }
    if (compartmentTonnage <= 0) {
      validationErrors.push(
        "Compartment tonnage must be greater than 0. Add fuel tanks first.",
      );
    }
  } else {
    if (selectedSystem.tonnage !== undefined && selectedSystem.tonnage < 0) {
      validationErrors.push("Tonnage cannot be negative.");
    }
    if (
      selectedSystem.tonnage === 0 &&
      selectedSystem.subtype !== "fuelScoop"
    ) {
      validationErrors.push("Tonnage must be greater than 0.");
    }
  }
  if (selectedSystem.cost !== undefined && selectedSystem.cost < 0) {
    validationErrors.push("Cost cannot be negative.");
  }
  if (selectedSystem.power !== undefined && selectedSystem.power < 0) {
    validationErrors.push("Power cannot be negative.");
  }
  // Duplicate check
  if (
    selectedSystem.subtype &&
    systems.some((s) => s.subtype === selectedSystem.subtype)
  ) {
    validationErrors.push("This fuel system is already installed.");
  }
  if (selectedSystem.subtype === "ramscoop" && isStreamlined) {
    // validationErrors.push("Ramscoop is not compatible with streamlined hulls.");
    // Instead, show a warning below
  }
  if (selectedSystem.subtype === "ramscoop" && totalRamscoopTonnage < 10) {
    validationErrors.push("Ramscoop must be at least 10 tons.");
  }
  if (selectedSystem.subtype === "ramscoop" && ramscoopCost <= 0) {
    validationErrors.push("Ramscoop cost must be greater than 0.");
  }

  // --- DIAGNOSTIC LOGS ---
  console.log("[FuelSystems] selectedSystem:", selectedSystem);
  console.log("[FuelSystems] validationErrors:", validationErrors);
  console.log(
    "[FuelSystems] Add button disabled:",
    validationErrors.length > 0,
  );
  if (selectedSystem.subtype === "ramscoop") {
    console.log("[FuelSystems] Ramscoop diagnostics:", {
      totalRamscoopTonnage,
      ramscoopCost,
      ramscoopCollection,
      selectedSystemTonnage: selectedSystem.tonnage,
      selectedSystemCost: selectedSystem.cost,
      selectedSystemProperties: selectedSystem.properties,
    });
  }
  // --- END DIAGNOSTIC LOGS ---

  const collapsibleTanksCapacity = systems
    .filter((s) => s.subtype === "collapsibleTank")
    .reduce((sum, s) => sum + (s.properties?.fullTonnage || 0), 0);
  const dropTanksCapacity = systems
    .filter((s) => s.subtype === "dropTank")
    .reduce((sum, s) => sum + (s.properties?.dropTankTonnage || 0), 0);
  const fuelCargoContainerCapacity = systems
    .filter(
      (s) =>
        s.subtype === "fuelCargoContainer" &&
        s.properties?.allocation === "fuel",
    )
    .reduce((sum, s) => sum + (s.properties?.capacity || 0), 0);

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Fuel Systems</h2>
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 bg-white dark:bg-gray-800">
        <h3 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Available Fuel Systems</h3>
        {/* Error Summary */}
        {showErrors && validationErrors.length > 0 && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-md p-4 mb-4">
            <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
              Please fix the following issues:
            </h4>
            <ul className="list-disc list-inside space-y-1">
              {validationErrors.map((error, index) => (
                <li key={index} className="text-sm text-red-700 dark:text-red-300">
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Streamlined hull warning for Ramscoop */}
        {selectedSystem.subtype === "ramscoop" && isStreamlined && (
          <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-md p-4 mb-4">
            <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
              Warning:
            </h4>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              Ramscoops are not compatible with atmospheric re-entry on
              streamlined hulls. Atmospheric entry will damage the ramscoop.
              This is allowed, but not recommended.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {availableSystems
            .filter((system) => techLevel >= system.techLevel)
            .map((system) => {
              const isInstalled = systems.some(
                (s) => s.subtype === system.subtype,
              );
              const inputValue = cardInputs[system.subtype] ?? 1;
              const error = cardErrors[system.subtype] ?? null;

              // Get precomputed stats based on system subtype
              let cardCost = system.cost;
              let cardTonnage = system.tonnage;
              let cardPower = system.power;

              if (system.subtype === "collapsibleTank") {
                cardTonnage = collapsibleTankStats.tonnage;
                cardCost = collapsibleTankStats.cost;
                cardPower = collapsibleTankStats.power;
              } else if (system.subtype === "fuelProcessor") {
                cardTonnage = fuelProcessorStats.tonnage;
                cardCost = fuelProcessorStats.cost;
                cardPower = fuelProcessorStats.power;
              } else if (system.subtype === "fuelTankCompartment") {
                cardTonnage = fuelTankCompartmentStats.tonnage;
                cardCost = fuelTankCompartmentStats.cost;
                cardPower = fuelTankCompartmentStats.power;
              } else if (system.subtype === "mountableTank") {
                cardTonnage = mountableTankStats.tonnage;
                cardCost = mountableTankStats.cost;
                cardPower = mountableTankStats.power;
              } else if (system.subtype === "dropTank") {
                cardTonnage = dropTankStats.tonnage;
                cardCost = dropTankStats.cost;
                cardPower = dropTankStats.power;
              } else if (system.subtype === "fuelCargoContainer") {
                cardTonnage = fuelCargoContainerStats.tonnage;
                cardCost = fuelCargoContainerStats.cost;
                cardPower = fuelCargoContainerStats.power;
              }

              // Add/Remove logic
              const handleAdd = () => {
                if (error) return;

                let properties = { ...system.properties };

                if (system.subtype === "collapsibleTank") {
                  properties = {
                    ...properties,
                    capacity: inputValue,
                  };
                } else if (system.subtype === "fuelCargoContainer") {
                  const allocation =
                    cardInputs[`${system.subtype}_allocation`] || "fuel";
                  properties = {
                    ...properties,
                    ...fuelCargoContainerStats,
                    allocation: allocation as "fuel" | "cargo",
                  };
                } else if (system.subtype === "fuelProcessor") {
                  properties = {
                    ...properties,
                    processingRate: fuelProcessorStats.processingRate,
                  };
                } else if (system.subtype === "fuelTankCompartment") {
                  properties = {
                    ...properties,
                    detectionPenalty: fuelTankCompartmentStats.detectionPenalty,
                  } as any;
                } else if (system.subtype === "metalHydride") {
                  properties = { ...system.properties };
                  // Do not set cost here; cost is handled globally in the summary.
                } else if (system.subtype === "dropTank") {
                  properties = {
                    ...properties,
                    ...dropTankStats,
                  };
                } else if (system.subtype === "ramscoop") {
                  properties = {
                    ...properties,
                    collectionRate: ramscoopCollection,
                  };
                }

                // Add more subtype-specific property logic as needed
                const systemToAdd: FuelSystem = {
                  id: `fuel-${Date.now()}`,
                  type: "fuel",
                  subtype: system.subtype,
                  name: system.name,
                  description: system.description,
                  cost: system.subtype === "ramscoop" ? ramscoopCost : cardCost,
                  tonnage:
                    system.subtype === "ramscoop"
                      ? totalRamscoopTonnage
                      : cardTonnage,
                  power: cardPower,
                  techLevel: techLevel,
                  properties,
                };
                dispatch(addFuelSystem(systemToAdd));
              };

              const isMetalHydride = system.subtype === "metalHydride";
              const cost = isMetalHydride
                ? 0.2 *
                  1000000 *
                  (shipState.fuelTanks +
                    collapsibleTanksCapacity +
                    dropTanksCapacity +
                    fuelCargoContainerCapacity)
                : system.cost;

              return (
                <div
                  key={system.subtype}
                  className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700"
                >
                  <div>
                    <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                      {system.name}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                      <div>Cost: {formatCurrency(cost)}</div>
                      <div>Tonnage: {cardTonnage}</div>
                      <div>Power: {cardPower}</div>
                    </div>
                    {/* Input fields for this system */}
                    {system.subtype === "collapsibleTank" && (
                      <div>
                        <TonnageInput
                          value={inputValue}
                          onChange={(value) => {
                            setCardInputs((inputs) => ({
                              ...inputs,
                              [system.subtype]: value,
                            }));
                            setCardErrors((errors) => ({
                              ...errors,
                              [system.subtype]: value < 1 ? "Tonnage must be at least 1" : null,
                            }));
                          }}
                          systemType="fuelTank"
                          category="fuelSystems"
                          label="Full Tank Tonnage"
                          placeholder="Enter tonnage"
                          className="text-xs"
                          showValidation={true}
                          disabled={isInstalled}
                        />
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Empty tank uses 1% of full tonnage.
                        </span>
                      </div>
                    )}
                    {system.subtype === "dropTank" && (
                      <div>
                        <TonnageInput
                          value={inputValue}
                          onChange={(value) => {
                            setCardInputs((inputs) => ({
                              ...inputs,
                              [system.subtype]: value,
                            }));
                            setCardErrors((errors) => ({
                              ...errors,
                              [system.subtype]: value < 1 ? "Tonnage must be at least 1" : null,
                            }));
                          }}
                          systemType="fuelTank"
                          category="fuelSystems"
                          label="Drop Tank Tonnage"
                          placeholder="Enter tonnage"
                          className="text-xs"
                          showValidation={true}
                          disabled={isInstalled}
                        />
                        <span className="text-xs text-gray-500">
                          Mount uses 0.4% of tank tonnage.
                        </span>
                      </div>
                    )}
                    {system.subtype === "fuelCargoContainer" && (
                      <div>
                        <TonnageInput
                          value={inputValue}
                          onChange={(value) => {
                            setCardInputs((inputs) => ({
                              ...inputs,
                              [system.subtype]: value,
                            }));
                            setCardErrors((errors) => ({
                              ...errors,
                              [system.subtype]: value < 1 ? "Capacity must be at least 1" : null,
                            }));
                          }}
                          systemType="fuelCapacity"
                          category="fuelSystems"
                          label="Container Capacity"
                          placeholder="Enter capacity"
                          className="text-xs"
                          showValidation={true}
                          disabled={isInstalled}
                        />
                        <label className="block text-xs font-medium text-gray-700 mt-2 mb-1">
                          Configure As
                        </label>
                        <select
                          value={
                            cardInputs[`${system.subtype}_allocation`] || "fuel"
                          }
                          onChange={(e) =>
                            setCardInputs((inputs) => ({
                              ...inputs,
                              [`${system.subtype}_allocation`]: e.target.value,
                            }))
                          }
                          className="w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm"
                          disabled={isInstalled}
                        >
                          <option value="fuel">Fuel</option>
                          <option value="cargo">Cargo</option>
                        </select>
                        <span className="text-xs text-gray-500">
                          Additional 5% tonnage for equipment.
                        </span>
                      </div>
                    )}
                    {system.subtype === "fuelProcessor" && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Processor Tonnage
                        </label>
                        <input
                          type="number"
                          value={inputValue}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            setCardInputs((inputs) => ({
                              ...inputs,
                              [system.subtype]: value,
                            }));
                            setCardErrors((errors) => ({
                              ...errors,
                              [system.subtype]:
                                value < 1 ? "Tonnage must be at least 1" : null,
                            }));
                          }}
                          className="w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm"
                          min="1"
                          step="0.1"
                          disabled={isInstalled}
                        />
                      </div>
                    )}
                    {system.subtype === "fuelScoop" && (
                      <div>
                      </div>
                    )}
                    {system.subtype === "fuelTankCompartment" && (
                      <div>
                        <TonnageInput
                          value={inputValue}
                          onChange={(value) => {
                            setCardInputs((inputs) => ({
                              ...inputs,
                              [system.subtype]: value,
                            }));
                            setCardErrors((errors) => ({
                              ...errors,
                              [system.subtype]: value < 1 ? "Tonnage must be at least 1" : null,
                            }));
                          }}
                          systemType="fuelTank"
                          category="fuelSystems"
                          label="Compartment Tonnage"
                          placeholder="Enter tonnage"
                          className="text-xs"
                          showValidation={true}
                          disabled={isInstalled}
                        />
                        <span className="text-xs text-gray-500">
                          Cr4,000 per ton. This will use {inputValue} tons of your fuel tankage to add {inputValue} tons of cargo space.
                        </span>
                      </div>
                    )}
                    {system.subtype === "metalHydride" && (
                      <div>
                        {/* Explanatory text remains below */}
                      </div>
                    )}
                    {system.subtype === "mountableTank" && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Tank Tonnage
                        </label>
                        <input
                          type="number"
                          value={inputValue}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            setCardInputs((inputs) => ({
                              ...inputs,
                              [system.subtype]: value,
                            }));
                            setCardErrors((errors) => ({
                              ...errors,
                              [system.subtype]:
                                value < 1 ? "Tonnage must be at least 1" : null,
                            }));
                          }}
                          className="w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm"
                          min="1"
                          step="0.1"
                          disabled={isInstalled}
                        />
                      </div>
                    )}
                    {system.subtype === "ramscoop" && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Additional Ramscoop Tonnage
                        </label>
                        <input
                          type="number"
                          value={inputValue}
                          onChange={(e) => {
                            const value = Math.max(0, Number(e.target.value));
                            setCardInputs((inputs) => ({
                              ...inputs,
                              [system.subtype]: value,
                            }));
                            setCardErrors((errors) => ({
                              ...errors,
                              [system.subtype]:
                                value < 0 ? "Tonnage cannot be negative" : null,
                            }));
                          }}
                          className="w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm"
                          min="0"
                          step="1"
                          disabled={isInstalled}
                        />
                        <span className="text-xs text-gray-500">
                          Base: 1% of ship tonnage + 5 tons (min 10 tons). Add
                          more if desired.
                        </span>
                        <div className="text-xs text-gray-500 mt-1">
                          Total Ramscoop:{" "}
                          <b>
                            {Math.max(10, Math.ceil(shipTonnage * 0.01 + 5)) +
                              (inputValue || 0)}{" "}
                            tons
                          </b>
                        </div>
                      </div>
                    )}
                    {error && (
                      <div className="text-xs text-red-600 mb-2">{error}</div>
                    )}
                  </div>
                  <button
                    className={`mt-2 px-3 py-1 rounded text-white font-semibold ${error ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                    onClick={handleAdd}
                    disabled={!!error}
                  >
                    Add
                  </button>
                </div>
              );
            })}
        </div>
      </div>

      {/* Selected Fuel Systems section removed */}
    </div>
  );
};

export default FuelSystems;
